import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Database, AlertCircle, RefreshCw, Loader2, Check, X, Trash2, BookOpen,
  User, UtensilsCrossed, Coffee
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_META = {
  veresiye:   { label: 'Veresiye',     icon: BookOpen,         color: 'amber' },
  users:      { label: 'Müşteriler',   icon: User,             color: 'sky' },
  menu_items: { label: 'A La Carte',   icon: UtensilsCrossed,  color: 'terracotta' },
  beverages:  { label: 'İçecekler',    icon: Coffee,           color: 'emerald' },
  catering:   { label: 'Catering',     icon: BookOpen,         color: 'rose' }
};

const COLOR_CLASSES = {
  amber:      { bg: 'bg-amber-50',      ring: 'ring-amber-200',      text: 'text-amber-700',      pill: 'bg-amber-200' },
  sky:        { bg: 'bg-sky-50',        ring: 'ring-sky-200',        text: 'text-sky-700',        pill: 'bg-sky-200' },
  terracotta: { bg: 'bg-orange-50',     ring: 'ring-orange-200',     text: 'text-orange-700',     pill: 'bg-orange-200' },
  emerald:    { bg: 'bg-emerald-50',    ring: 'ring-emerald-200',    text: 'text-emerald-700',    pill: 'bg-emerald-200' },
  rose:       { bg: 'bg-rose-50',       ring: 'ring-rose-200',       text: 'text-rose-700',       pill: 'bg-rose-200' }
};

const DataQuality = ({ token }) => {
  const [data, setData] = useState({ counts: { total: 0 }, issues: {} });
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('veresiye');
  const [editing, setEditing] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/data-quality`, { headers });
      setData(res.data);
    } catch (e) {
      toast.error('Veri kalitesi bilgisi alınamadı');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveVeresiye = async (id, payload) => {
    try {
      await axios.put(`${API}/admin/data-quality/veresiye/${id}`, payload, { headers });
      toast.success('Güncellendi');
      setEditing(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Güncellenemedi'); }
  };

  const saveUser = async (id, payload) => {
    try {
      await axios.put(`${API}/admin/data-quality/user/${id}`, payload, { headers });
      toast.success('Güncellendi');
      setEditing(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Güncellenemedi'); }
  };

  const deleteVeresiye = async (id) => {
    if (!window.confirm('Bu veresiye kaydını silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/data-quality/veresiye/${id}`, { headers });
      toast.success('Silindi');
      fetchData();
    } catch (e) { toast.error('Silinemedi'); }
  };

  const total = data.counts.total || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-playfair text-2xl font-bold text-charcoal flex items-center gap-2">
            <Database className="w-6 h-6 text-terracotta" />Veri Kalitesi
          </h2>
          <p className="text-charcoal/60 text-sm">Eksik veya tutarsız kayıtları toparlayın</p>
        </div>
        <Button onClick={fetchData} variant="outline" disabled={loading} data-testid="dq-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Yenile</span>
        </Button>
      </div>

      {/* Top summary */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className={`rounded-2xl p-5 ${total === 0 ? 'bg-emerald-50 ring-1 ring-emerald-200' : 'bg-gradient-to-r from-amber-50 to-orange-50 ring-1 ring-amber-200'}`}
        data-testid="dq-summary">
        <div className="flex items-center gap-3">
          {total === 0 ? <Check className="w-7 h-7 text-emerald-600" /> : <AlertCircle className="w-7 h-7 text-amber-600" />}
          <div>
            <p className="font-playfair text-xl font-bold">
              {total === 0 ? 'Veri kalitesi mükemmel!' : `${total} kayıtta eksik bilgi var`}
            </p>
            <p className="text-charcoal/60 text-sm">
              {total === 0 ? 'Tüm kayıtlarınız eksiksiz görünüyor.' : 'Aşağıdan inceleyip tek tıkla düzeltebilirsiniz.'}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Section tabs */}
      <div className="flex flex-wrap gap-2 border-b border-stone-200 pb-2">
        {Object.entries(SECTION_META).map(([key, meta]) => {
          const count = data.counts[key] || 0;
          const Icon = meta.icon;
          const isActive = activeSection === key;
          return (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium transition-all ${
                isActive ? 'bg-charcoal text-white shadow' : 'bg-white text-charcoal/70 hover:bg-stone-100 ring-1 ring-stone-200'
              }`}
              data-testid={`dq-tab-${key}`}
            >
              <Icon className="w-4 h-4" />
              {meta.label}
              {count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${isActive ? 'bg-white/20' : COLOR_CLASSES[meta.color].pill}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Items list */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-charcoal/40 mx-auto" /></div>
        ) : (data.issues[activeSection] || []).length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center text-charcoal/50 ring-1 ring-stone-200">
            <Check className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
            <p className="font-medium">Bu bölümde sorun yok!</p>
          </div>
        ) : (
          (data.issues[activeSection] || []).map((it, idx) => {
            const isEditing = editing?.id === it.id;
            const meta = SECTION_META[activeSection];
            const colors = COLOR_CLASSES[meta.color];
            return (
              <motion.div
                key={it.id || idx}
                initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                className={`rounded-xl p-3 ring-1 ${colors.bg} ${colors.ring} flex flex-wrap items-center gap-3`}
                data-testid={`dq-row-${activeSection}-${idx}`}
              >
                <div className="flex-1 min-w-[200px]">
                  <p className="font-semibold text-charcoal truncate">{it.label}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {it.problems.map((p, i) => (
                      <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full ${colors.pill} ${colors.text} font-medium`}>
                        {p}
                      </span>
                    ))}
                  </div>
                </div>

                {isEditing && activeSection === 'veresiye' && (
                  <div className="flex flex-wrap gap-2 w-full md:w-auto">
                    <Input placeholder="İsim" value={editing.customer_name || ''} onChange={(e) => setEditing({ ...editing, customer_name: e.target.value })} className="w-40 h-9" data-testid={`dq-edit-name-${idx}`} />
                    <Input placeholder="Telefon" value={editing.customer_phone || ''} onChange={(e) => setEditing({ ...editing, customer_phone: e.target.value })} className="w-36 h-9" data-testid={`dq-edit-phone-${idx}`} />
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-9" onClick={() => saveVeresiye(it.id, { customer_name: editing.customer_name, customer_phone: editing.customer_phone })} data-testid={`dq-save-${idx}`}>
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-9" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button>
                  </div>
                )}

                {isEditing && activeSection === 'users' && (
                  <div className="flex flex-wrap gap-2 w-full md:w-auto">
                    <Input placeholder="İsim" value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-40 h-9" data-testid={`dq-edit-name-${idx}`} />
                    <Input placeholder="Telefon" value={editing.phone || ''} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="w-36 h-9" data-testid={`dq-edit-phone-${idx}`} />
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-9" onClick={() => saveUser(it.id, { name: editing.name, phone: editing.phone })} data-testid={`dq-save-${idx}`}>
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-9" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button>
                  </div>
                )}

                {!isEditing && (activeSection === 'veresiye' || activeSection === 'users') && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing({
                      id: it.id,
                      customer_name: it.label === '(isim yok)' ? '' : it.label,
                      customer_phone: it.phone || '',
                      name: it.label === '(?)' ? '' : it.label,
                      phone: it.phone || ''
                    })} data-testid={`dq-edit-btn-${idx}`}>
                      Düzelt
                    </Button>
                    {activeSection === 'veresiye' && (
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => deleteVeresiye(it.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                )}

                {!isEditing && (activeSection === 'menu_items' || activeSection === 'beverages') && (
                  <p className="text-xs text-charcoal/50 italic">İlgili sekmeden düzenleyin</p>
                )}
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DataQuality;
